import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../utils/AuthContext';
import { db } from '../../utils/firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { getSupportedTimezones, isValidIanaTimezone } from '../../utils/userTimezone';
import { Loader2, Save, CheckCircle } from 'lucide-react';
import './UserProfileFields.css';

export interface UserProfileFormValues {
  phone: string;
  location: string;
  timezone: string;
}

interface Props {
  /** When set, edits that user (admin). Otherwise edits signed-in user. */
  targetUid?: string;
  /** Hide save button and use external save via ref/callback — default shows inline save. */
  showSaveButton?: boolean;
  onSaved?: () => void;
}

const UserProfileFields: React.FC<Props> = ({ targetUid, showSaveButton = true, onSaved }) => {
  const { user } = useAuth();
  const uid = targetUid || user?.id;
  const [values, setValues] = useState<UserProfileFormValues>({
    phone: '',
    location: '',
    timezone: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  const timezones = useMemo(() => getSupportedTimezones(), []);

  useEffect(() => {
    if (!uid) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', uid));
        if (cancelled) return;
        if (snap.exists()) {
          const data = snap.data();
          setValues({
            phone: typeof data.phone === 'string' ? data.phone : '',
            location: typeof data.location === 'string' ? data.location : '',
            timezone: typeof data.timezone === 'string' ? data.timezone : '',
          });
        }
      } catch (err) {
        console.error('[UserProfileFields] Load error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uid]);

  const handleSave = useCallback(async () => {
    if (!uid) return;
    setError(null);
    if (values.timezone.trim() && !isValidIanaTimezone(values.timezone.trim())) {
      setError('Please choose a valid timezone.');
      setSaveStatus('error');
      return;
    }
    setSaving(true);
    setSaveStatus('idle');
    try {
      await updateDoc(doc(db, 'users', uid), {
        phone: values.phone.trim() || null,
        location: values.location.trim() || null,
        timezone: values.timezone.trim() || null,
      });
      setSaveStatus('saved');
      onSaved?.();
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err) {
      console.error('[UserProfileFields] Save error:', err);
      setSaveStatus('error');
      setError('Could not save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [uid, values, onSaved]);

  if (!uid) return null;

  if (loading) {
    return (
      <div className="user-profile-fields user-profile-fields--loading">
        <Loader2 size={20} className="user-profile-fields-spin" aria-hidden />
        <span>Loading profile…</span>
      </div>
    );
  }

  return (
    <div className="user-profile-fields">
      <p className="user-profile-fields-desc">
        Shown to teammates when they hover your avatar in comments.
      </p>
      <div className="user-profile-fields-grid">
        <div className="user-profile-field">
          <label htmlFor="upf-phone">Phone</label>
          <input
            id="upf-phone"
            type="tel"
            value={values.phone}
            onChange={(e) => {
              setValues((v) => ({ ...v, phone: e.target.value }));
              setSaveStatus('idle');
            }}
            placeholder="+1 (555) 123-4567"
          />
        </div>
        <div className="user-profile-field">
          <label htmlFor="upf-location">Location</label>
          <input
            id="upf-location"
            type="text"
            value={values.location}
            onChange={(e) => {
              setValues((v) => ({ ...v, location: e.target.value }));
              setSaveStatus('idle');
            }}
            placeholder="New York, USA"
          />
        </div>
        <div className="user-profile-field user-profile-field--full">
          <label htmlFor="upf-timezone">Timezone</label>
          <select
            id="upf-timezone"
            value={values.timezone}
            onChange={(e) => {
              setValues((v) => ({ ...v, timezone: e.target.value }));
              setSaveStatus('idle');
            }}
          >
            <option value="">Not set</option>
            {timezones.map((tz) => (
              <option key={tz} value={tz}>
                {tz.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </div>
      </div>
      {error && (
        <p className="user-profile-fields-error" role="alert">
          {error}
        </p>
      )}
      {showSaveButton && (
        <div className="user-profile-fields-actions">
          <button
            type="button"
            className="user-profile-fields-save"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <Loader2 size={14} className="user-profile-fields-spin" aria-hidden />
                Saving…
              </>
            ) : saveStatus === 'saved' ? (
              <>
                <CheckCircle size={14} aria-hidden />
                Saved
              </>
            ) : (
              <>
                <Save size={14} aria-hidden />
                Save profile
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default UserProfileFields;
